import localForage from 'localforage';
import { refreshAccessToken, RefreshTokenResponse } from '@/legacy';
import { deleteCookie } from '@/cookie';
import { FetchResponse } from '@/types';

export enum COOKIES_NAMES {
  PARDOT_COOKIE_VISITOR_ID = 'visitor_id1002231',
  ENVIRONMENT = 'environment',
  INTERNAL = 'internal',
  SHOW_POST_LOGIN = 'SHOW_POST_LOGIN',
  SIDEBAR_TOUR_MODE_SHOWN = 'SIDEBAR_TOUR_MODE_SHOWN',
  PAYMENT_LINKS_TOUR_MODE_SHOWN = 'PAYMENT_LINKS_TOUR_MODE_SHOWN',
  LIVE_MODE = 'liveMode',
  SHOW_KYC_RENEWAL = 'SHOW_KYC_RENEWAL',
  BIZID = 'bizid'
}

const accessTokenExpiryKey = 'access_token_expiry';
const refreshTokenKey = 'refresh_token';
const refreshTokenExpiryKey = 'refresh_token_expiry';

// Some keys are legacy, will be removed in the future
const localStorageKeys = [
  accessTokenExpiryKey,
  refreshTokenKey,
  refreshTokenExpiryKey,
  'user',
  'business',
  'dashboardLevel',
  'is_rcbc_sub_account',
  'LAST_ACTION',
  'is_password_expired',
  'apc_user_id',
  'WHITELIST_REMINDED_BUSINESSES',
  'BALANCE_SWITCH_BANNER_CLOSED',
  'billingReminder',
  'PREV_TRANSACTION_ENV',
  'LEAD_QUALIFICATION_DATA',
  'refresh_token',
  'access_token_expiry',
  'refresh_token_expiry',
  'MOBILE_APP_BANNER_DO_NOT_SHOW_AGAIN',
  'pending_redirect_path',
  'maintenance_banner',
  'isUtv',
  'transaction_dt',
  'PAGE_CURSOR_DATA',
  'one_time_token',
  'RO_DEPRECATION_NOTIFYING_BUSINESSES',
  'VA_DEPRECATION_NOTIFYING_BUSINESSES',
  'regional.account',
  'regional.business',
  'regional.user',
  'regional.productPermissions',
  'regional.xp_sub_account',
  'login.email'
];

const localForageKeys = [
  'LAST_BALANCE_DATA',
  'LAST_BR_NEW_DATA',
  'pending_redirect_path'
];

export const localStorageCleaner = (): void => {
  localStorageKeys.forEach(key => localStorage.removeItem(key));
  localForageKeys.forEach(key => localForage.removeItem(key));
};

export const saveRefreshTokenExpiry = (expiry: string) => {
  localStorage.setItem(refreshTokenExpiryKey, expiry);
};

export const getRefreshTokenExpiry = () => {
  return localStorage.getItem(refreshTokenExpiryKey);
};

export const saveAccessTokenExpiry = (expiry: string) => {
  localStorage.setItem(accessTokenExpiryKey, expiry);
};

export const getAccessTokenExpiry = () => {
  return localStorage.getItem(accessTokenExpiryKey);
};

export const tokenExpiryIn = (token: string): number => {
  if (!token) return 0;
  const [, body] = token.split('.');
  if (!body) return 0;
  const decodedBody = JSON.parse(Buffer.from(body, 'base64').toString('utf-8'));
  const BUFFER = 5; // buffer 5 seconds
  return (decodedBody.exp - decodedBody.iat - BUFFER) * 1000;
};

export const isTokenExpired = (key: string) => {
  const tokenExpiry = localStorage.getItem(key);
  if (!tokenExpiry) return false;
  const expiryInMs = Number(tokenExpiry);
  return Date.now() - expiryInMs >= 0;
};

export const getAccessToken = () => {
  return window.accessToken && !isTokenExpired(accessTokenExpiryKey)
    ? window.accessToken
    : null;
};

export const getRefreshToken = () => {
  const refreshToken = localStorage.getItem(refreshTokenKey);
  return refreshToken && !isTokenExpired(refreshTokenExpiryKey)
    ? refreshToken
    : null;
};

export const saveTokensAndExpiries = ({
  access_token,
  refresh_token,
  start_time
}: {
  access_token: string;
  refresh_token?: string;
  start_time: number;
}) => {
  if (access_token) {
    const accessTokenExpireInMs = tokenExpiryIn(access_token);
    const accessTokenExpiry = start_time + accessTokenExpireInMs;
    saveAccessTokenExpiry(accessTokenExpiry.toString());
    window.accessToken = access_token;
  }

  if (refresh_token) {
    const refreshTokenExpireInMs = tokenExpiryIn(refresh_token);
    const refreshTokenExpiry = start_time + refreshTokenExpireInMs;
    saveRefreshTokenExpiry(refreshTokenExpiry.toString());
    localStorage.setItem(refreshTokenKey, refresh_token);
  }
};

let lastTimeChecked: number | null = null;
let scheduledWakeUps: NodeJS.Timeout[] = [];
let scheduledRefreshes: NodeJS.Timeout[] = [];

/**
 * Clear all scheduled on-wake-up's
 */
const clearScheduledOnWakeUps = () => {
  scheduledWakeUps.forEach(scheduledWakeUp => {
    clearInterval(scheduledWakeUp);
  });
  scheduledWakeUps = [];
};
/**
 * Clear all scheduled token refresh
 */
const clearScheduledRefreshes = () => {
  scheduledRefreshes.forEach(scheduledRefresh => {
    clearTimeout(scheduledRefresh);
  });
  scheduledRefreshes = [];
};

const clearScheduledJobs = () => {
  clearScheduledOnWakeUps();
  clearScheduledRefreshes();
};

export const cleanupLocalData = () => {
  // TODO: dismiss toast
  deleteCookie(COOKIES_NAMES.ENVIRONMENT);
  deleteCookie(COOKIES_NAMES.LIVE_MODE);
  deleteCookie(COOKIES_NAMES.SHOW_KYC_RENEWAL);
  deleteCookie(COOKIES_NAMES.BIZID);
  clearScheduledJobs();
  delete window.accessToken;
  localStorageCleaner();
};

/**
 * The function to save tokens (Access Token and Refresh Token) and schedule the next token refresh
 *
 */
export const saveTokensAndScheduleRefresh = ({
  access_token,
  refresh_token,
  start_time
}: {
  access_token: string;
  refresh_token?: string;
  start_time?: number;
}) => {
  clearScheduledRefreshes();
  const now = Date.now();
  start_time = start_time || now;
  saveTokensAndExpiries({ access_token, refresh_token, start_time });
  const accessTokenExpiry = tokenExpiryIn(access_token);
  scheduleOnWakeUp();
  // minus the latency of the API call
  const duration = now - start_time;
  const timeout = duration ? accessTokenExpiry - duration : accessTokenExpiry;
  const scheduledRefresh = setTimeout(async () => {
    // clear the scheduled refresh in case there is already one;
    await refreshTokenOrCleanup();
  }, timeout);
  scheduledRefreshes.push(scheduledRefresh);
};

/**
 * Attempts to refresh the access token using the stored refresh token and
 * reschedules the next refresh. On failure the local auth data is cleaned up.
 */
const refreshTokenOrCleanup = async () => {
  const refreshToken = getRefreshToken();
  try {
    // no refresh token means the session expired
    if (!refreshToken) throw new Error('session expired');
    const response = await refreshAccessToken(refreshToken);
    const { access_token, _start: start_time } =
      response as RefreshTokenResponse & FetchResponse;
    if (!access_token) throw new Error('error refreshing token');
    saveTokensAndScheduleRefresh({ access_token, start_time });
  } catch {
    cleanupLocalData();
  }
};

// There is situation when user put the computer to sleep. In this case, need to refresh the token on wake up
export const scheduleOnWakeUp = () => {
  const fiveSecondsInMillis = 5000;
  if (scheduledWakeUps.length > 0) return;
  const scheduledWakeUp = setInterval(async () => {
    const now = Date.now();
    // Can consider the computer sleep for more than 5 seconds
    if (lastTimeChecked && now - lastTimeChecked >= fiveSecondsInMillis) {
      // Then refresh to token now
      await refreshTokenOrCleanup();
    }
    lastTimeChecked = now;
  }, 1000); // run this every 1 seconds
  scheduledWakeUps.push(scheduledWakeUp);
};
